<script>
  import UppyDropzone from "#lib/dropzone/UppyDropzone.svelte";
  import { SUPABASE_URL as supabaseUrl } from "#src/env";
  import { createGroup } from "@local/lamina-core";

  import { Modal } from "svelte-utilities";
  import InfoIcon from "@local/assets/icons/info.svg";
  import TextInput from "#lib/sidebar/TextInput.svelte";
  import DateInput from "#lib/sidebar/DateInput.svelte";
  import ContenteditableInput from "#lib/sidebar/ContenteditableInput.svelte";
  import { jobs } from "#src/stores/review-jobs.js";

  import { submitTallyRequest, api } from "#src/api/";
  import { draftTallyRequest } from "#src/stores/ui.js";
  import { alerts } from "#src/stores/alerts.js";

  export let org_id;
  export let customer_id = null;
  export let loggedIn = false;
  export let balances = null;

  const definedScopes = [
    "metal_windows",
    "vinyl_windows",
    "storefront",
    "railings",
    "curtain_wall",
    "shower_doors",
    "interior_partitions",
    "doors",
    "mirrors",
    "decorative_glass",
    "skylights",
    "louvers",
  ];

  if (!$draftTallyRequest) {
    $draftTallyRequest = tallyRequest();
  }
  let sent = false;
  let dateModal;

  // `<input type="date">` speaks "YYYY-MM-DD" in the user's local timezone, so build
  // the bound value from local parts. `toISOString()` would convert to UTC first and
  // shift the date by a day for anyone whose offset crosses midnight.
  function isoLocalDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  function displayDate(isoDate) {
    const [year, month, day] = isoDate.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString();
  }

  function getThreeBusinessDaysFromNow() {
    const date = new Date();
    let added = 0;
    while (added < 3) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) {
        added++;
      }
    }
    return isoLocalDate(date);
  }

  $: requireName = !loggedIn;
  $: minDate = getThreeBusinessDaysFromNow();
  // `min` on the date input only restricts the calendar popup; a typed date can still
  // land below it, and a restored draft can go stale. Compare explicitly — both sides
  // are "YYYY-MM-DD", so they order lexicographically.
  $: deadlineTooSoon = !!$draftTallyRequest.data.deadline && $draftTallyRequest.data.deadline < minDate;
  $: disabled =
    !$draftTallyRequest.data.name ||
    (requireName && !$draftTallyRequest.data.customer_name) ||
    !$draftTallyRequest.data.files.length ||
    !$draftTallyRequest.data.scope.length ||
    !$draftTallyRequest.data.deadline ||
    deadlineTooSoon ||
    !$draftTallyRequest.data.acknowledge_tally_services ||
    !$draftTallyRequest.data.acknowledge_terms;
  $: selectedScopes = new Set($draftTallyRequest.data.scope);
  $: allSelected = definedScopes.every((s) => selectedScopes.has(s));

  function structuredFiles(files) {
    const tree = { is_directory: true };

    for (const file of files) {
      if (!file.path) {
        tree[file.name] = file;
        continue;
      }

      const parts = file.path.split("/");
      let current = tree;

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (i === parts.length - 1) {
          // It's a file
          current[part] = file;
        } else {
          // It's a directory
          if (!current[part]) {
            current[part] = { is_directory: true };
          }
          current = current[part];
        }
      }
    }

    return tree;
  }

  function renderedFiles(tree, indent = 0) {
    let result = [];

    Object.keys(tree)
      .sort()
      .forEach((key) => {
        if (key !== "is_directory") {
          if (typeof tree[key] === "object" && tree[key].is_directory) {
            // It's a directory
            result.push({ type: "directory", name: key, indent });
            const children = renderedFiles(tree[key], indent + 1);
            result.push(...children);
          } else {
            // It's a file
            result.push({ type: "file", name: key, file: tree[key], indent });
          }
        }
      });

    return result;
  }

  function clearForm() {
    $draftTallyRequest = tallyRequest();
  }

  function tallyRequest() {
    return {
      id: crypto.randomUUID(),
      organization_id: org_id,
      job_id: /** @type {string | null} */ (null),
      customer_id,
      created_at: new Date(),
      status: "pending",
      data: {
        name: "",
        customer_name: "",
        deadline: null,
        additional_notes: "",
        acknowledge_tally_services: false,
        acknowledge_terms: false,
        scope: [],
        files: /** @type {{name: string, path: string, contentType: string}[]} */ ([]),
      },
    };
  }

  function addFiles(e) {
    const { results } = e.detail;
    $draftTallyRequest.data.files.push(
      ...results.map((r) => {
        return {
          name: r.name,
          path: r.meta.relativePath,
          contentType: r.meta.contentType,
        };
      }),
    );

    $draftTallyRequest = $draftTallyRequest;
  }

  function updateScope(scope, value) {
    const selected = new Set($draftTallyRequest.data.scope);
    if (value) {
      selected.add(scope);
    } else {
      selected.delete(scope);
    }

    $draftTallyRequest.data.scope = definedScopes.filter((s) => selected.has(s));
  }

  async function submitRequest() {
    if (disabled) {
      if (!$draftTallyRequest.data.customer_name && requireName) {
        alerts.add({ type: "error", message: `Customer Name is required`, timeout: 5000 });
      }

      if (!$draftTallyRequest.data.name) {
        alerts.add({ type: "error", message: `Project Name is required`, timeout: 5000 });
      }

      if (!$draftTallyRequest.data.files.length) {
        alerts.add({ type: "error", message: `No files have been uploaded`, timeout: 5000 });
      }

      if (!$draftTallyRequest.data.scope.length) {
        alerts.add({ type: "error", message: `At least one scope item must be selected`, timeout: 5000 });
      }

      if (!$draftTallyRequest.data.deadline) {
        alerts.add({ type: "error", message: `Requested Completion Date is required`, timeout: 5000 });
      } else if (deadlineTooSoon) {
        alerts.add({
          type: "error",
          message: `Requested Completion Date must be ${displayDate(minDate)} or later — we ask for at least three business days`,
          timeout: 5000,
        });
      }

      if (!$draftTallyRequest.data.acknowledge_tally_services) {
        alerts.add({
          type: "error",
          message: `You must acknowledge the Tally Services disclaimer`,
          timeout: 5000,
        });
      }

      if (!$draftTallyRequest.data.acknowledge_terms) {
        alerts.add({
          type: "error",
          message: `You must agree to the Shape Exchange Cloud Services Agreement`,
          timeout: 5000,
        });
      }

      return;
    }

    const res = await submitTallyRequest($draftTallyRequest);
    if (res.success) {
      sent = true;
    } else {
      console.log(res.error);
      alerts.add({ type: "error", message: `Error submitting tally request` });
    }
    $draftTallyRequest = tallyRequest();
  }
</script>

{#if !sent}
  <div class="grow overflow-auto bg-gray-100 flex justify-center">
    <div class="space-y-4 p-4 my-8 max-w-full content-container">
      <div class="w-full space-y-4 text-sm">
        <div class="px-4 pb-4 border-b border-gray-300 flex items-center justify-between">
          <h2>Job Request Form</h2>
          {#if balances && balances.balance_status !== "uninitialized"}
            <div>
              <span class="text-sm">Tally Job Credits:</span>
              {#if balances.balance_status === "pending"}
                <span class="text-gray-500 italic">Pending</span>
              {:else if balances.balance_status === "settled"}
                <span class="font-bold">{balances.balance}</span>
              {/if}
            </div>
          {/if}
        </div>
        {#if requireName}
          <TextInput
            labelWidth="7rem"
            border
            label="Your Name"
            bind:value={$draftTallyRequest.data.customer_name} />
        {/if}
        <TextInput labelWidth="7rem" border label="Project Name" bind:value={$draftTallyRequest.data.name} />
        <div class="p-2 bg-white border rounded border-gray-300">
          <div class="text-gray-400 flex-none mb-2 flex justify-between items-center">
            <div>Included Scope</div>
            <button>
              {#if allSelected}
                <button class="btn-text text-xs" on:click={() => ($draftTallyRequest.data.scope = [])}
                  >Deselect All</button>
              {:else}
                <button
                  class="btn-text text-xs"
                  on:click={() => ($draftTallyRequest.data.scope = [...definedScopes])}>Select All</button>
              {/if}
            </button>
          </div>
          <div class="flex gap-4">
            <div class="space-y-2 basis-1/2 checkbox-container">
              <div>
                <input
                  type="checkbox"
                  checked={selectedScopes.has("metal_windows")}
                  on:input={(e) => updateScope("metal_windows", e.target.checked)} />
                <div>Windows (Metal)</div>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={selectedScopes.has("vinyl_windows")}
                  on:input={(e) => updateScope("vinyl_windows", e.target.checked)} />
                <div>Windows (Vinyl)</div>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={selectedScopes.has("storefront")}
                  on:input={(e) => updateScope("storefront", e.target.checked)} />
                <div>Storefront</div>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={selectedScopes.has("railings")}
                  on:input={(e) => updateScope("railings", e.target.checked)} />
                <div>Railings</div>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={selectedScopes.has("curtain_wall")}
                  on:input={(e) => updateScope("curtain_wall", e.target.checked)} />
                <div>Curtain Wall</div>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={selectedScopes.has("shower_doors")}
                  on:input={(e) => updateScope("shower_doors", e.target.checked)} />
                <div>Shower Doors</div>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={selectedScopes.has("interior_partitions")}
                  on:input={(e) => updateScope("interior_partitions", e.target.checked)} />
                <div>Interior Partitions</div>
              </div>
            </div>
            <div class="space-y-2 basis-1/2 checkbox-container">
              <div>
                <input
                  type="checkbox"
                  checked={selectedScopes.has("doors")}
                  on:input={(e) => updateScope("doors", e.target.checked)} />
                <div>Doors</div>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={selectedScopes.has("mirrors")}
                  on:input={(e) => updateScope("mirrors", e.target.checked)} />
                <div>Mirrors</div>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={selectedScopes.has("decorative_glass")}
                  on:input={(e) => updateScope("decorative_glass", e.target.checked)} />
                <div>Decorative Glass</div>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={selectedScopes.has("skylights")}
                  on:input={(e) => updateScope("skylights", e.target.checked)} />
                <div>Skylights</div>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={selectedScopes.has("louvers")}
                  on:input={(e) => updateScope("louvers", e.target.checked)} />
                <div>Louvers</div>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={selectedScopes.has("other")}
                  on:input={(e) => updateScope("other", e.target.checked)} />
                <div>Other (note below)</div>
              </div>
            </div>
          </div>
        </div>
        <ContenteditableInput
          labelWidth="7rem"
          border
          label="Additional notes"
          bind:value={$draftTallyRequest.data.additional_notes} />
        <div class="relative">
          <DateInput
            labelWidth="12rem"
            border
            label="Requested Completion Date"
            min={minDate}
            bind:value={$draftTallyRequest.data.deadline} />
          <button
            class="absolute right-0 top-0 mt-1.5 mr-2 p-1 rounded text-gray-500 hover:bg-gray-200"
            on:click={() => dateModal.open()}>
            <InfoIcon />
          </button>
        </div>
        <div class="p-2 bg-white border rounded border-gray-300 flex items-start">
          <div class="text-gray-400 flex-none" style="width: 8rem">Uploaded Files</div>
          <div class="text-xs grow">
            {#if !$draftTallyRequest.data.files.length}
              <div class="italic text-gray-500">None</div>
            {:else}
              {@const files = renderedFiles(structuredFiles($draftTallyRequest.data.files))}
              <div>
                {#each files as file}
                  <div style="padding-left: {file.indent * 1.5}rem">
                    {#if file.type === "directory"}
                      <div class="font-semibold">📁 {file.name}</div>
                    {:else}
                      <div class="flex items-center gap-2">
                        <div class="truncate">📄 {file.name}</div>
                      </div>
                    {/if}
                  </div>
                {/each}
              </div>
            {/if}
          </div>
        </div>
      </div>
      <div class="max-w-lg w-full text-sm border border-gray-300 rounded bg-white p-4 overflow-hidden">
        {#key $draftTallyRequest.id}
          <UppyDropzone
            uploadUrl={`${supabaseUrl}/storage/v1/upload/resumable`}
            bucket="temp"
            folder={$draftTallyRequest.id}
            height={350}
            on:complete={addFiles} />
        {/key}
      </div>
      <div class="p-2 bg-white border rounded border-gray-300 flex gap-2 items-start">
        <input
          class="mt-1"
          type="checkbox"
          bind:checked={$draftTallyRequest.data.acknowledge_tally_services} />
        <div class="text-xs">
          I understand that Tally Services are document-processing and data-entry services intended to assist
          with estimating and bidding and are not substitutes for my or my team’s professional judgment or
          independent scope, quantity, cost, and/or constructibility analysis.
        </div>
      </div>
      <div class="p-2 bg-white border rounded border-gray-300 flex gap-2 items-start">
        <input class="mt-1" type="checkbox" bind:checked={$draftTallyRequest.data.acknowledge_terms} />
        <div class="text-xs">
          I agree to the terms of the <a
            href="https://help.shape.exchange/legal-csa"
            class="btn-text"
            target="_blank">Shape Exchange Cloud Services Agreement</a
          >, including the Additional Terms and Conditions for Tally Services
        </div>
      </div>

      <div class="pb-8 flex justify-between gap-2">
        {#if loggedIn}
          <div class="basis-1/2">
            <button class="btn-text text-sm px-4" on:click={clearForm}>Clear Form</button>
          </div>
        {/if}
        <button class="btn btn-primary px-6 grow" class:disabled on:click={submitRequest}>
          Submit Request
        </button>
      </div>
    </div>
  </div>
{:else}
  <div class="grow overflow-auto bg-gray-100 flex justify-center">
    <div class="space-y-4 p-4 mt-8 max-w-full content-container">
      <div class="w-full sm:w-124 space-y-4 text-sm">
        <div class="px-4 pb-4 border-b border-gray-300">
          <h2>Tally Request Submitted</h2>
        </div>

        <p>
          Your tally request has been submitted successfully. We will review your request and get back to you
          shortly.
        </p>
        <div class="flex gap-2">
          <button class="btn btn-primary px-6 w-full" on:click={() => (sent = false)}>
            Submit Another Request
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<Modal bind:this={dateModal} closeable buttons={[{ label: "OK", type: "confirm", style: "primary" }]}>
  <div slot="title">Completion Date Availability</div>
  <div slot="content" class="space-y-4 text-xs">
    <p>
      Our team will review your requested completion date and confirm availability once we have assessed the
      scope of your project.
    </p>
    <p>
      If you require an expedited completion date, please contact our support team at <a
        href="mailto:info@shape.exchange"
        class="btn-text">support@shape.exchange</a> to discuss your needs, and we will respond as quickly as possible.
    </p>
  </div>
</Modal>

<style lang="scss">
  .content-container {
    @media (min-width: 640px) {
      width: 32rem;
    }
  }

  .checkbox-container > div {
    @apply flex items-center gap-2 text-xs;
  }
</style>
